/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Carousel,
  DatePicker,
  Form,
  Grid,
  Input,
  InputNumber,
  Message,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  TableColumnProps,
  Tag,
  Typography
} from '@arco-design/web-react';
import { IconDelete } from '@arco-design/web-react/icon';
import dayjs from 'dayjs';

import './index.scss';
import { API_CONFIG } from '../utils';

// import { sessionList } from '../const';
import { get, post } from '../requestNew';
import type { PenaltyLogItem } from '../penalty-log/types';
import Capital from './Capital';
import { StatusEnum, StockOrder } from './types';
const RadioGroup = Radio.Group;
const Text = Typography.Text;
const Row = Grid.Row;
const Col = Grid.Col;
const Option = Select.Option;
const FormItem = Form.Item;

/** 列表接口 `/sys/umsExplodeOrder/list` 排序参数：asc 升序、desc 降序；不传表示不按该字段排序 */
type ExplodeOrderListSortParams = {
  totalAssetsSort?: 'asc' | 'desc';
  todayEarningSort?: 'asc' | 'desc';
  totalEarningSort?: 'asc' | 'desc';
};

function listSortFromTableSorter(sorter: {
  field?: string | number;
  direction?: 'ascend' | 'descend';
}): ExplodeOrderListSortParams {
  const direction = sorter?.direction;
  if (!direction) return {};
  const api: 'asc' | 'desc' = direction === 'ascend' ? 'asc' : 'desc';
  const field = sorter?.field;
  if (field === 'totalAssets') return { totalAssetsSort: api };
  if (field === 'todayEarningRate') return { todayEarningSort: api };
  if (field === 'totalEarningRate') return { totalEarningSort: api };
  return {};
}

const formItemLayout = {
  labelCol: {
    span: 8
  },
  wrapperCol: {
    span: 16
  }
};

/** 筛选栏标签字号（略小于正文） */
const filterLabelStyle = { fontSize: 12 };
const options = [
  { label: '全部', value: StatusEnum.all },
  { label: '待确认', value: StatusEnum.unconfirmed },
  { label: '已确认', value: StatusEnum.confirm }
];

const trueIdentifyOption = [
  { label: '全部', value: '' },
  { label: '正确', value: StatusEnum.confirm },
  { label: '不正确', value: StatusEnum.unconfirmed }
];

/** 红黄牌列表筛选：全部不传 penaltyType；0 无牌 1 黄牌 2 红牌 */
const penaltyTypeOption = [
  { label: '全部', value: '' },
  { label: '无牌', value: 0 },
  { label: '黄牌', value: 1 },
  { label: '红牌', value: 2 }
];

/** 与红黄牌管理页一致：处罚原因多选可选项 */
const ISSUE_PENALTY_REASON_OPTIONS = [
  '有资金进出未上报',
  '报单不规范',
  '报单不规范，无净资产',
  '报单不规范，无总资产',
  '报单不规范，无持仓数据',
  '资金少于1万，不符合参赛资格',
  '选手主动要求退赛',
  '当日未报单',
  '连续获得3张黄牌',
  '累计获得6张黄牌',
  '连续获得5张黄牌',
  '累计获得15张黄牌',
  '选手未配合验证账户',
  '笔记不符合社区规定'
];

function isPenaltyFilterAllOrNone(filter: number | string): boolean {
  if (filter === '' || filter == null) return true;
  const n = Number(filter);
  return n === 0;
}

function isPenaltyFilterYellowOrRed(filter: number | string): boolean {
  const n = Number(filter);
  return n === 1 || n === 2;
}

function Main(props: any) {
  const { groupTypeOption } = props;
  const [form] = Form.useForm<StockOrder>();
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState<number | null>(null);
  const [currentPicIndex, setCurrentPicIndex] = useState(0);
  const [sessionList, setSessionList] = useState<{ label: string; value: string }[]>([]);
  const [gameSession, setGameSession] = useState('');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [gameName, setGameName] = useState('');
  /** 列表接口筛选；不填则传空字符串 */
  const [openIdFilter, setOpenIdFilter] = useState('');
  /** 服务端分页：当前页、每页条数、总数 */
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [group, groupChange] = useState('');
  const [trueIdentify, trueIdentifyChange] = useState<number | string>('');
  const [penaltyTypeFilter, setPenaltyTypeFilter] = useState<number | string>('');
  const [curIdx, setCurIdx] = useState(0);
  const [curFormData, setCurFormData] = useState<any>({});
  const [dataList, setDataList] = useState<StockOrder[]>([]);
  const [visible, setVisible] = useState(false);
  const [dateVisible, setDateVisible] = useState(false);
  const [toDate, setToDate] = useState('');
  const [style, setStyle] = useState({});

  /** 报单页：处罚选手弹窗（与 penalty-log 一致） */
  const [penaltyIssueVisible, setPenaltyIssueVisible] = useState(false);
  const [penaltyIssueGameSession, setPenaltyIssueGameSession] = useState('');
  const [penaltyIssuePenaltyDate, setPenaltyIssuePenaltyDate] = useState('');
  const [penaltyIssuePenaltyReason, setPenaltyIssuePenaltyReason] = useState<string[]>([]);
  const [penaltyIssuePenaltyReasonError, setPenaltyIssuePenaltyReasonError] = useState(false);
  const [penaltyIssueRemark, setPenaltyIssueRemark] = useState('');

  /** 报单页：编辑处罚弹窗 */
  const [penaltyEditVisible, setPenaltyEditVisible] = useState(false);
  const [penaltyEditRecord, setPenaltyEditRecord] = useState<PenaltyLogItem | null>(null);
  const [penaltyEditReason, setPenaltyEditReason] = useState('');
  const [penaltyEditRemark, setPenaltyEditRemark] = useState('');

  /** 与服务端 list 接口三个排序字段一致；空对象表示三列均不参与服务端排序 */
  const [listSort, setListSort] = useState<ExplodeOrderListSortParams>({});

  const contentRef = useRef<any>(null);

  const tableColumns = useMemo((): TableColumnProps[] => {
    const order = (v?: 'asc' | 'desc'): 'ascend' | 'descend' | undefined =>
      v === 'asc' ? 'ascend' : v === 'desc' ? 'descend' : undefined;
    return [
      {
        title: '参赛名',
        dataIndex: 'gameName',
        width: 100
      },
      {
        title: 'openId',
        dataIndex: 'openId',
        width: 120
      },
      {
        title: '总资产(元)',
        dataIndex: 'totalAssets',
        width: 130,
        sorter: true,
        sortOrder: order(listSort.totalAssetsSort)
      },
      {
        title: '今收益(%)',
        dataIndex: 'todayEarningRate',
        width: 130,
        sorter: true,
        sortOrder: order(listSort.todayEarningSort)
      },
      {
        title: '总收益(%)',
        dataIndex: 'totalEarningRate',
        width: 130,
        sorter: true,
        sortOrder: order(listSort.totalEarningSort)
      },
      {
        title: '用户确认',
        dataIndex: 'isTrueIdentify',
        width: 90,
        render: (_, item) => {
          if (item.isTrueIdentify === StatusEnum.confirm) {
            return <Text type="primary">正确</Text>;
          } else if (item.isTrueIdentify === StatusEnum.unconfirmed) {
            return <Text type="error">不正确</Text>;
          } else {
            return '';
          }
        }
      },
      {
        title: '状态',
        width: 80,
        render: (_, item) => {
          return item.checkStatus === StatusEnum.confirm ? (
            <Text type="primary">已确认</Text>
          ) : (
            <Text type="error">待确认</Text>
          );
        }
      }
    ];
  }, [listSort]);

  const nickNameChange = (val: string) => {
    setGameName(val);
  };
  const statusChange = (val: string) => {
    setStatus(val);
  };

  const isConfirm = dataList[curIdx]?.checkStatus === StatusEnum.confirm;
  const isTrue = dataList[curIdx]?.isTrueIdentify === StatusEnum.confirm;

  // 状态 / 用户确认 / 参赛名 / openId / 分组 / 赛季 / 日期 / 红黄牌 均走服务端筛选
  const setForm = () => {
    if (dataList.length) {
      getData(dataList[curIdx].openId, dataList[curIdx].gameName);
    }
  };
  const prev = () => {
    if (curIdx) {
      setCurIdx(curIdx - 1);
    } else {
      Message.error('已是第一条！');
    }
  };
  const next = () => {
    if (curIdx < dataList.length - 1) {
      setCurIdx(curIdx + 1);
    } else {
      Message.error('已是最后一条！');
    }
  };

  const exportExcel = () => {
    window.open(
      `${API_CONFIG.exportBaseUrl}/sys/umsExplodeOrder/exportOrder?holdDate=${date}&groupType=${group}&gameSession=${gameSession}`
    );
  };

  const getData = (openId: string, gameName: string) => {
    const params = {
      holdDate: date,
      openId,
      gameSession
    };
    get('/sys/umsExplodeOrder/getExplodeOrderDetail', params)
      .then((res: any) => {
        const { code, data } = res || {};
        if (code === 200 && data?.orderDetails) {
          const { orderDetails = {} } = data || {};
          setCurFormData(orderDetails);
          form.setFieldsValue({ ...orderDetails, gameName });
        }
      })
      .catch(e => {
        Message.error('网络错误！');
        console.log(e);
      });
  };

  const getList = (page = pageNum, size = pageSize, sort?: ExplodeOrderListSortParams) => {
    if (!group) return;
    const effectiveSort = sort !== undefined ? sort : listSort;
    const params: Record<string, unknown> = {
      holdDate: date,
      groupType: group,
      gameSession,
      pageNum: page,
      pageSize: size,
      gameName,
      openId: openIdFilter.trim()
    };
    if (effectiveSort.totalAssetsSort) {
      params.totalAssetsSort = effectiveSort.totalAssetsSort;
    }
    if (effectiveSort.todayEarningSort) {
      params.todayEarningSort = effectiveSort.todayEarningSort;
    }
    if (effectiveSort.totalEarningSort) {
      params.totalEarningSort = effectiveSort.totalEarningSort;
    }
    if (penaltyTypeFilter !== '' && penaltyTypeFilter != null) {
      const pt = Number(penaltyTypeFilter);
      if (pt === 1 || pt === 2 || pt === 0) {
        params.penaltyType = pt;
      }
    }
    // 状态：'' 表全部不传；0 待确认；1 已确认
    if (status !== '' && status != null) {
      const cs = Number(status);
      if (cs === 0 || cs === 1) {
        params.checkStatus = cs;
      }
    }
    // 用户确认：'' 表全部不传；0 不正确；1 正确
    if (trueIdentify !== '' && trueIdentify != null) {
      const ti = Number(trueIdentify);
      if (ti === 0 || ti === 1) {
        params.isTrueIdentify = ti;
      }
    }
    setDataList([]);
    setLoading(true);
    get('/sys/umsExplodeOrder/list', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const list = data?.list ?? data?.records ?? [];
          setTotal(Number(data?.total ?? data?.totalCount ?? list.length ?? 0));
          if (list.length) {
            setCurIdx(0);
            setDataList(list);
            getData(list[0].openId, list[0].gameName);
          } else {
            setCurIdx(0);
            setDataList([]);
            setCurFormData({});
            form.resetFields();
          }
        } else {
          Message.error(message);
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      })
      .finally(() => setLoading(false));
  };

  const submit = (changeStatus?: boolean) => {
    form
      .validate()
      .then(formData => {
        const checkStatus = changeStatus
          ? isConfirm
            ? StatusEnum.unconfirmed
            : StatusEnum.confirm
          : dataList[curIdx].checkStatus;
        // 修复全部删除时holdStockVos属性不存在 导致删除不成功问题
        const holdStockVos = formData.holdStockVos || [];
        const data: any = {
          ...dataList[curIdx],
          ...curFormData,
          ...formData,
          holdStockVos,
          picType: 1,
          isFront: 0,
          holdDate: date,
          checkStatus,
          gameSession
        };
        const totalAssets = formData.totalAssets;

        const params = changeStatus ? {id:data.id ,totalAssets} : data;
        const urlReq = changeStatus ? '/sys/umsExplodeOrder/updateExplodeOrderStatus' : '/sys/umsExplodeOrder/dealExplodeOrder';

        post(urlReq, params)
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              const { todayEarningRate = '', totalEarningRate = '' } = data;
              Message.success('提交成功！');
              const listClone = [...dataList];
              listClone[curIdx] = { ...dataList[curIdx], checkStatus, todayEarningRate, totalEarningRate };
              setDataList(listClone);
            } else {
              Message.error(message);
            }
          })
          .catch(e => {
            console.log(e);
          });
      })
      .catch(e => {
        Message.error('字段填写不完整，请核对后再次提交！');
        console.log(e, '校验失败');
      });
  };

  const dateChangeCancel = () => {
    setDateVisible(false);
    setToDate('');
  };

  const changeDate = () => {
    if (!toDate) {
      Message.error('请先选择日期');
      return;
    }
    const data = {
      gameSession,
      openId: dataList[curIdx].openId,
      fromDate: date,
      toDate
    };
    post('/sys/umsExplodeOrder/updateExportOrderHoldDate', data).then(res => {
      console.log(res);
      if (res) {
        Message.success('操作成功');
        dateChangeCancel();
        getList();
      }
    });
  };

  const urlProcess = (url:any) =>{
    // https://fulicupdev.oss-cn-hangzhou.aliyuncs.com/2026-01-19/ebf18a76-388a-4566-9a51-13c1ac768b9d.jpg?x-oss-process=image/resize,h_1800,m_lfit
    if (url.indexOf('?x-oss-process') > 0) {
      return url.split('?x-oss-process')[0];
    }else{
      return url;
    }
  };

  const analysis = (pic: { url: string; id: number }) => {
    setAnalysisLoading(pic.id);
    post('/common/imageIdentify', { url: pic.url })
      .then((res: any) => {
        const { code, message, data } = res || {};
        if (code === 200) {
          const { totalAssets, stockPercent, todayProfitLoss } = data;
          const holdStockVos = data.holdStockVos || [];
          holdStockVos.forEach((item: any) => {
            item.type = item.profitAndLossPrice;
            item.holdPrice = item.holdPrice || '';
            item.profitAndLossPrice = item.profitAndLossPrice || '';
            item.stockName = item.stockName || '';
          });
          if (totalAssets === null) {
            Message.error('识别出错！');
          } else {
            form.setFieldsValue({ totalAssets, holdStockVos, stockPercent, todayProfitLoss });
            Message.success('识别完成！');
          }
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误！');
      })
      .finally(() => {
        setAnalysisLoading(null);
      });
  };
  const handleDelete = () => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除当前报单吗？',
      onOk: () => {
        const id = dataList[curIdx]?.id;
        if (!id) {
          Message.error('缺少删除ID');
          return;
        }
        post('/sys/umsExplodeOrder/deleteExplodeOrder', { id })
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success('删除成功！');
              // 更新数据列表
              const newDataList = [...dataList];
              newDataList.splice(curIdx, 1);
              setDataList(newDataList);
              // 如果删除的是最后一条数据，切换到前一条
              if (curIdx >= newDataList.length && newDataList.length > 0) {
                setCurIdx(newDataList.length - 1);
                getData(newDataList[newDataList.length - 1].openId, newDataList[newDataList.length - 1].gameName);
              } else if (newDataList.length > 0) {
                getData(newDataList[curIdx].openId, newDataList[curIdx].gameName);
              }
            } else {
              Message.error(message || '删除失败！');
            }
          })
          .catch(() => {
            Message.error('网络错误！');
          });
      }
    });
  };

  const handleResetEarningRate = () => {
    const id = dataList[curIdx]?.id;
    if (!id) {
      Message.error('缺少报单ID');
      return;
    }
    Modal.confirm({
      title: '收益率置零',
      content: '确认将该报单今日收益率和总收益率置为 0？（仅当 holdDate 之前无有效上一日报单时生效）',
      onOk: () =>
        post('/sys/umsExplodeOrder/resetEarningRateIfNoPreviousOrder', { id })
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success('收益率已置零');
              const listClone = [...dataList];
              listClone[curIdx] = {
                ...dataList[curIdx],
                todayEarningRate: 0,
                totalEarningRate: 0
              };
              setDataList(listClone);
              form.setFieldsValue({ todayEarningRate: 0, totalEarningRate: 0 });
            } else {
              Message.error(message || '操作失败');
            }
          })
          .catch(() => {
            Message.error('网络错误！');
          })
    });
  };

  const handleRecalculateEarningRate = () => {
    const openId = (dataList[curIdx]?.openId || curFormData?.openId || '').trim();
    if (!openId) {
      Message.error('当前报单缺少 openId，请等待详情加载完成或重新选择一条报单');
      return;
    }
    if (!gameSession) {
      Message.error('请选择赛季');
      return;
    }
    Modal.confirm({
      title: '收益率重算',
      content: '确认同步该用户本赛季所有笔记日期，并重新计算收益率？',
      onOk: () =>
        post(
          `/sys/umsExplodeOrder/syncNotebookDateWithExplodeOrder?gameSession=${encodeURIComponent(gameSession)}&openId=${encodeURIComponent(openId)}`
        )
          .then((res: any) => {
            const { code, message, data } = res || {};
            if (code === 200) {
              Message.success(data != null ? `操作成功，已更新 ${data} 条笔记` : '操作成功');
              getData(openId, dataList[curIdx]?.gameName || curFormData?.gameName || '');
              getList();
            } else {
              Message.error(message || '操作失败');
            }
          })
          .catch(() => {
            Message.error('网络错误！');
          })
    });
  };

  const openPenaltyIssueModal = () => {
    if (!(dataList[curIdx]?.openId || curFormData?.openId)) {
      Message.error('当前报单缺少 openId，请等待详情加载完成或重新选择一条报单');
      return;
    }
    setPenaltyIssueVisible(true);
    setPenaltyIssueGameSession(gameSession);
    setPenaltyIssuePenaltyDate(date);
    setPenaltyIssuePenaltyReason([]);
    setPenaltyIssuePenaltyReasonError(false);
    setPenaltyIssueRemark('');
  };

  const closePenaltyIssueModal = () => {
    setPenaltyIssueVisible(false);
    setPenaltyIssuePenaltyReason([]);
    setPenaltyIssuePenaltyReasonError(false);
    setPenaltyIssueRemark('');
  };

  const issuePenaltyCard = (type: 1 | 2) => {
    const openId = (dataList[curIdx]?.openId || curFormData?.openId || '').trim();
    if (!openId) {
      Message.error('当前报单缺少 openId，请等待详情加载完成或重新选择一条报单');
      return;
    }
    const session = penaltyIssueGameSession || gameSession;
    if (!session) {
      Message.error('请选择处罚赛季');
      return;
    }
    if (!penaltyIssuePenaltyDate) {
      Message.error('请选择处罚日期');
      return;
    }
    if (!penaltyIssuePenaltyReason.length) {
      setPenaltyIssuePenaltyReasonError(true);
      Message.error('请选择处罚原因，至少选择一项');
      return;
    }
    const url = type === 1 ? '/sys/umsUserPenaltyLog/issueYellowCard' : '/sys/umsUserPenaltyLog/issueRedCard';
    post(url, {
      openId,
      gameSession: session,
      penaltyDate: penaltyIssuePenaltyDate,
      penaltyReason: penaltyIssuePenaltyReason.join(';'),
      remark: penaltyIssueRemark.trim()
    })
      .then((res: any) => {
        if (res?.code === 200) {
          Message.success(type === 1 ? '下发黄牌成功' : '下发红牌成功');
          closePenaltyIssueModal();
          getList();
        } else {
          Message.error(res?.message || '操作失败');
        }
      })
      .catch(() => Message.error('网络错误'));
  };

  /** 当前报单行 openId + 页面赛季/日期 + 筛选项红黄牌类型，查询单条处罚记录 */
  const fetchPenaltyLogByCurrentRow = (): Promise<PenaltyLogItem | null> => {
    const row = dataList[curIdx];
    const openId = (row?.openId || curFormData?.openId || '').trim();
    if (!openId) {
      Message.error('当前报单缺少 openId，请等待详情加载完成或重新选择一条报单');
      return Promise.resolve(null);
    }
    const pt = Number(penaltyTypeFilter);
    if (pt !== 1 && pt !== 2) {
      Message.error('请先在筛选项中选择黄牌或红牌');
      return Promise.resolve(null);
    }
    return get('/sys/umsUserPenaltyLog/getByUserDay', {
      openId,
      gameSession,
      penaltyDate: date,
      penaltyType: pt
    })
      .then((res: any) => {
        if (res?.code === 200 && res.data) {
          return res.data as PenaltyLogItem;
        }
        Message.error(res?.message || '未找到处罚记录');
        return null;
      })
      .catch(() => {
        Message.error('网络错误');
        return null;
      });
  };

  const openPenaltyEditModal = () => {
    fetchPenaltyLogByCurrentRow().then(rec => {
      if (!rec) return;
      setPenaltyEditRecord(rec);
      setPenaltyEditReason(rec.penaltyReason ?? '');
      setPenaltyEditRemark(rec.remark ?? '');
      setPenaltyEditVisible(true);
    });
  };

  const closePenaltyEditModal = () => {
    setPenaltyEditVisible(false);
    setPenaltyEditRecord(null);
    setPenaltyEditReason('');
    setPenaltyEditRemark('');
  };

  const submitPenaltyEdit = () => {
    if (!penaltyEditRecord?.id) return;
    post('/sys/umsUserPenaltyLog/updatePenaltyReasonAndRemark', {
      id: penaltyEditRecord.id,
      penaltyReason: penaltyEditReason,
      remark: penaltyEditRemark
    })
      .then((res: any) => {
        if (res?.code === 200) {
          Message.success('更新成功');
          closePenaltyEditModal();
          getList();
        } else {
          Message.error(res?.message || '更新失败');
        }
      })
      .catch(() => Message.error('网络错误'));
  };

  const handlePenaltyCancelClick = () => {
    fetchPenaltyLogByCurrentRow().then(rec => {
      if (!rec?.id) return;
      Modal.confirm({
        title: '提示',
        content: '确定取消该条处罚？',
        focusLock: true,
        onOk: () =>
          post('/sys/umsUserPenaltyLog/cancelPenalty', { id: rec.id }).then((res: any) => {
            if (res?.code === 200) {
              Message.success('取消处罚成功');
              getList();
            } else {
              Message.error(res?.message || '操作失败');
            }
          }),
      });
    });
  };

  useEffect(() => {
    setCurrentPicIndex(0);
    setForm();
  }, [curIdx]);

  useEffect(() => {
    if (groupTypeOption.length) {
      groupChange(groupTypeOption[0].value);
    }
  }, [groupTypeOption]);

  useEffect(() => {
    if (!gameSession) return;
    setPageNum(1);
    getList(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, group, gameSession, gameName, openIdFilter, penaltyTypeFilter, status, trueIdentify]);
  useEffect(() => {
    get('/gameConfig/getUmsGameConfigList').then((res: any) => {
      if (res && res.data ) {
        const list = res.data.map((item: any) => {
          const { gameSession } = item;
          return { label: gameSession, value: gameSession };
        });
        const session: any = res.data.find((item: any) => dayjs().isAfter(dayjs(item.startTime))) || {};

        setSessionList(list);
        setGameSession(session.gameSession);
      }
    });

    const rect = contentRef.current && contentRef.current.getBoundingClientRect();
    const clientWidth = rect.width;
    const clientHeight = rect.height;
    const scale = clientWidth / 1600;
    setStyle({
      transform: `scale(${scale})`,
      height: `${clientHeight / scale}px`
    });
  }, []);

  return (
    <div className="main" ref={contentRef}>
      <div className="scale" style={style}>
        <div className="left">
          <Typography.Text style={{ display: 'none' }}>版本 v15</Typography.Text>
          <Space style={{ marginBottom: 20 }}>
            <Typography.Text style={filterLabelStyle}>赛季:</Typography.Text>
            <Select onChange={setGameSession} value={gameSession} style={{ width: 100 }}>
              {sessionList.map(item => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
            <Typography.Text style={filterLabelStyle}>时间:</Typography.Text>
            <DatePicker style={{ width: 150 }} defaultValue={dayjs().format('YYYY-MM-DD')} onChange={setDate} />

            <Typography.Text style={filterLabelStyle}>参赛名:</Typography.Text>
            <Input onChange={nickNameChange} style={{ width: 120 }} />
            <Typography.Text style={filterLabelStyle}>openId:</Typography.Text>
            <Input
              value={openIdFilter}
              onChange={setOpenIdFilter}
              style={{ width: 120 }}
              allowClear
            />
          </Space>
          <Space style={{ marginBottom: 20 }}>
            <Typography.Text style={filterLabelStyle}>状态:</Typography.Text>
            <RadioGroup size='mini' options={options} type="button" value={status} onChange={statusChange} />
            <Typography.Text style={filterLabelStyle}>分组:</Typography.Text>
            <RadioGroup size='mini' options={groupTypeOption} type="button" value={group} onChange={groupChange} />
            <Typography.Text style={filterLabelStyle}>红黄牌:</Typography.Text>
            <RadioGroup
              size='mini'
              options={penaltyTypeOption}
              type="button"
              value={penaltyTypeFilter}
              onChange={setPenaltyTypeFilter}
            />
          </Space>
          <Space style={{ marginBottom: 20 }}>
          <Typography.Text style={filterLabelStyle}>用户确认:</Typography.Text>
            <RadioGroup
              size='mini'
              options={trueIdentifyOption}
              type="button"
              defaultValue={trueIdentify}
              onChange={trueIdentifyChange}
            />
            <Button
              type="primary"
              size='mini'
              onClick={() => {
                exportExcel();
              }}
            >
              数据导出
            </Button>
          </Space>
          <div className="table">
            <Table
              loading={loading}
              columns={tableColumns}
              data={dataList}
              onChange={(_pagination, sorter, _filters, extra) => {
                if (extra.action !== 'sort') return;
                const s = Array.isArray(sorter) ? sorter[0] : sorter;
                const next = listSortFromTableSorter(
                  s as { field?: string | number; direction?: 'ascend' | 'descend' }
                );
                setListSort(next);
                setPageNum(1);
                getList(1, pageSize, next);
              }}
              pagination={{
                current: pageNum,
                pageSize,
                total,
                sizeCanChange: true,
                sizeOptions: [20, 50, 100, 200],
                showTotal: true,
                pageSizeChangeResetCurrent: true,
                onChange: (page, size) => {
                  const nextSize = size && size !== pageSize ? size : pageSize;
                  const sizeChanged = !!(size && size !== pageSize);
                  const nextPage = sizeChanged ? 1 : page;
                  setPageNum(nextPage);
                  if (sizeChanged) setPageSize(nextSize);
                  getList(nextPage, nextSize);
                }
              }}
              rowKey="id"
              scroll={{ y: true }}
              rowClassName={record => {
                return record.openId === curFormData.openId ? 'gray' : '';
              }}
              onRow={(record: StockOrder) => ({
                onClick: () => {
                  const idx = dataList.findIndex(item => item.openId === record.openId);
                  if (curIdx === idx) {
                    form.setFieldsValue(record);
                  } else {
                    setCurIdx(idx);
                  }
                }
              })}
            />
          </div>
        </div>
        <div className="right">
          {dataList.length ? (
            <Form form={form} autoComplete="off" {...formItemLayout}>
              <Row gutter={24}>
                <Col span={12}>
                  <FormItem label="参赛名" field="gameName">
                    <Input disabled />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem label="今日收益率" field="todayEarningRate">
                    <InputNumber hideControl suffix="%" />
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <FormItem label="总收益率" field="totalEarningRate">
                    <InputNumber hideControl suffix="%" />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem label="今日总资产" field="totalAssets" rules={[{ required: true }]}>
                    <InputNumber hideControl />
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <FormItem label="仓位" field="stockPercent">
                    <InputNumber hideControl suffix="%" />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem label="今日盈亏" field="todayProfitLoss">
                    <InputNumber hideControl />
                  </FormItem>
                </Col>
              </Row>
              <FormItem shouldUpdate noStyle>
                {(values: StockOrder) => {
                  const currentPic = values.orderPicVos?.[currentPicIndex];
                  return (
                    <div className="order-carousel-wrap">
                      <Carousel
                        autoPlay={false}
                        animation="card"
                        indicatorPosition="outer"
                        style={{ width: '100%', background: '#ccc' }}
                        arrowClassName="carousel"
                        onChange={index => setCurrentPicIndex(index)}
                      >
                        {values.orderPicVos?.map(item => (
                          <div key={item.id} style={{ width: 300 }}>
                            <img src={urlProcess(item.url)} style={{ width: '100%' }} />
                          </div>
                        ))}
                      </Carousel>
                      <Button
                        className="order-carousel-analysis"
                        type="primary"
                        status="danger"
                        size="mini"
                        loading={currentPic ? analysisLoading === currentPic.id : false}
                        disabled={!currentPic}
                        onClick={() => currentPic && analysis(currentPic)}
                      >
                        分析
                      </Button>
                    </div>
                  );
                }}
              </FormItem>
              <FormItem style={{ marginBottom: 0 }} label="持仓列表" className="stock-list">
                <Form.List field="holdStockVos">
                  {(fields, { add, remove }) => {
                    return (
                      <div>
                        {fields.map((item, index) => {
                          return (
                            <div key={item.key}>
                              <FormItem>
                                {index === 0 && (
                                  <div>
                                    <Space style={{ color: '#666' }}>
                                      <FormItem noStyle>
                                        <div style={{ width: 150 }}>股票名称</div>
                                      </FormItem>
                                      <FormItem noStyle>
                                        <div style={{ width: 150 }}>持仓(元)</div>
                                      </FormItem>
                                      {/* <FormItem noStyle>
                                    <div style={{ width: 150 }}>盈亏(万)</div>
                                  </FormItem> */}
                                      <FormItem noStyle>
                                        <Button
                                          style={{ opacity: 0 }}
                                          icon={<IconDelete />}
                                          shape="circle"
                                          status="danger"
                                        ></Button>
                                      </FormItem>
                                    </Space>
                                  </div>
                                )}
                                <Space>
                                  <FormItem field={item.field + '.stockName'} rules={[{ required: true }]} noStyle>
                                    <Input style={{ width: 150 }} />
                                  </FormItem>
                                  <FormItem field={item.field + '.holdPrice'} rules={[{ required: true }]} noStyle>
                                    <Input style={{ width: 150 }} />
                                  </FormItem>
                                  {/* <FormItem
                                field={item.field + '.profitAndLossPrice'}
                                rules={[{ required: true }]}
                                noStyle
                              >
                                <Input style={{ width: 150 }} />
                              </FormItem> */}
                                  <Button
                                    icon={<IconDelete />}
                                    shape="circle"
                                    status="danger"
                                    onClick={() => remove(index)}
                                  ></Button>
                                </Space>
                              </FormItem>
                            </div>
                          );
                        })}
                        <FormItem>
                          <Button
                            onClick={() => {
                              add();
                            }}
                          >
                            添加
                          </Button>
                        </FormItem>
                      </div>
                    );
                  }}
                </Form.List>
              </FormItem>
              <FormItem label=" ">
                <div>
                  <Space style={{ marginBottom: 10 }}>
                    <Tag size="large" color={isTrue ? 'blue' : 'red'} bordered>
                      用户确认{isTrue ? '正确' : '不正确'}
                    </Tag>
                    <Tag size="large" color={isConfirm ? 'blue' : 'red'} bordered>
                      当前数据{isConfirm ? '已确认' : '待确认'}
                    </Tag>

                    <Button
                      type="outline"
                      onClick={() => {
                        prev();
                      }}
                    >
                      前一条
                    </Button>
                    <Button
                      type="outline"
                      onClick={() => {
                        next();
                      }}
                    >
                      后一条
                    </Button>
                  </Space>
                </div>

                <Space>
                  <Button type="primary" status={isConfirm ? 'danger' : 'default'} onClick={() => submit(true)}>
                    {isConfirm ? '取消确认' : '确认'}
                  </Button>

                  <Button type="outline" onClick={() => setVisible(true)}>
                    修改资金流入
                  </Button>

                  <Button type="outline" onClick={() => submit(false)}>
                    修改数据
                  </Button>

                  <Button type="outline" onClick={() => setDateVisible(true)}>
                    数据迁移
                  </Button>

                  <Button type="outline" status="danger" onClick={() => handleDelete()}>删除</Button>
                </Space>
                <Space style={{ marginTop: 10 }}>
                  <Button type="outline" onClick={handleResetEarningRate}>
                    收益率置零
                  </Button>
                  <Button type="outline" onClick={handleRecalculateEarningRate}>
                    收益率重算
                  </Button>
                  {isPenaltyFilterAllOrNone(penaltyTypeFilter) ? (
                    <Button type="outline" status="danger" onClick={openPenaltyIssueModal}>
                      处罚选手
                    </Button>
                  ) : null}
                  {isPenaltyFilterYellowOrRed(penaltyTypeFilter) ? (
                    <>
                      <Button type="outline" status="default" onClick={openPenaltyEditModal}>
                        修改处罚
                      </Button>
                      <Button type="outline" status="success" onClick={handlePenaltyCancelClick}>
                        取消处罚
                      </Button>
                    </>
                  ) : null}
                </Space>
              </FormItem>
            </Form>
          ) : (
            '无数据'
          )}
        </div>
      </div>

      <Modal
        title="处罚选手"
        visible={penaltyIssueVisible}
        onCancel={closePenaltyIssueModal}
        footer={null}
        unmountOnExit
        style={{ width: 500 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>参赛名:</Typography.Text>
          <Typography.Text style={{ display: 'inline-block', width: 280, verticalAlign: 'top' }}>
            {dataList[curIdx]?.gameName || curFormData?.gameName || '-'}
          </Typography.Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚赛季:</Typography.Text>
          <Select
            value={penaltyIssueGameSession}
            onChange={setPenaltyIssueGameSession}
            style={{ width: 280 }}
            placeholder="请选择"
          >
            {sessionList.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚日期:</Typography.Text>
          <DatePicker
            value={penaltyIssuePenaltyDate ? dayjs(penaltyIssuePenaltyDate) : undefined}
            onChange={(val: any) =>
              setPenaltyIssuePenaltyDate(val ? dayjs(val).format('YYYY-MM-DD') : '')
            }
            style={{ width: 280 }}
            placeholder="请选择日期"
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80, verticalAlign: 'top', paddingTop: 6 }}>
            <span style={{ color: 'rgb(var(--red-6))', marginRight: 2 }}>*</span>
            处罚原因:
          </Typography.Text>
          <span style={{ display: 'inline-block', verticalAlign: 'top' }}>
            <Select
              mode="multiple"
              value={penaltyIssuePenaltyReason}
              onChange={(v: string[]) => {
                setPenaltyIssuePenaltyReason(v);
                if (v.length) setPenaltyIssuePenaltyReasonError(false);
              }}
              placeholder="必填，请至少选择一项"
              style={{ width: 280 }}
              allowClear
              status={penaltyIssuePenaltyReasonError ? 'error' : undefined}
            >
              {ISSUE_PENALTY_REASON_OPTIONS.map(text => (
                <Option key={text} value={text}>
                  {text}
                </Option>
              ))}
            </Select>
            {penaltyIssuePenaltyReasonError ? (
              <Typography.Text type="error" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                请选择处罚原因，至少选择一项
              </Typography.Text>
            ) : null}
          </span>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚备注:</Typography.Text>
          <Input
            value={penaltyIssueRemark}
            onChange={setPenaltyIssueRemark}
            placeholder="请输入..."
            style={{ width: 280 }}
          />
        </div>
        <Space style={{ marginTop: 24 }}>
          <Button type="primary" status="danger" onClick={() => issuePenaltyCard(2)}>
            下发红牌
          </Button>
          <Button
            type="primary"
            style={{ background: '#ffb117', borderColor: '#ffb117' }}
            onClick={() => issuePenaltyCard(1)}
          >
            下发黄牌
          </Button>
          <Button onClick={closePenaltyIssueModal}>取消</Button>
        </Space>
      </Modal>

      <Modal
        title="处罚编辑"
        visible={penaltyEditVisible}
        onCancel={closePenaltyEditModal}
        onOk={submitPenaltyEdit}
        unmountOnExit
        okText="确定"
        style={{ width: 500 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>参赛名:</Typography.Text>
          <Typography.Text style={{ display: 'inline-block', width: 280, verticalAlign: 'top' }}>
            {penaltyEditRecord?.gameName || '-'}
          </Typography.Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚原因:</Typography.Text>
          <Input
            value={penaltyEditReason}
            onChange={setPenaltyEditReason}
            placeholder="请输入..."
            style={{ width: 280 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚备注:</Typography.Text>
          <Input
            value={penaltyEditRemark}
            onChange={setPenaltyEditRemark}
            placeholder="请输入..."
            style={{ width: 280 }}
          />
        </div>
      </Modal>

      <Capital
        openId={dataList[curIdx]?.openId}
        orderId={dataList[curIdx]?.orderId}
        nickname={dataList[curIdx]?.nickname}
        visible={visible}
        holdDate={date}
        gameSession={gameSession}
        onClose={() => setVisible(false)}
      />

      <Modal
        title="选择日期"
        visible={dateVisible}
        style={{ width: 260 }}
        onOk={() => changeDate()}
        onCancel={() => dateChangeCancel()}
        maskClosable={false}
      >
        <div>移动到日期</div>
        <DatePicker value={toDate} onChange={setToDate} />
      </Modal>
    </div>
  );
}

export default Main;
